import type { ChartConfiguration } from '@/pages/reports/types';

/**
 * A single dimension available for reports.
 * Maps to ReportDimension dataclass in metadata_service.py
 */
export interface ReportDimension {
  /** Dimension identifier (e.g., "ahoy_first_name" or custom property name) */
  name: string;
  /** Human-readable label */
  title: string;
  /** Field type for native reporting: "string" | "number" | "time" | "boolean" */
  cube_type: 'string' | 'number' | 'time' | 'boolean';
  /** Entity type this dimension belongs to */
  object_type: string;
  /** Whether this is a builtin, custom, cube-native, or association-based dimension */
  source: 'builtin' | 'custom' | 'cube' | 'association';
  /** The property_type_name for custom properties, or builtin property name. May be null for some builtin properties. */
  property_type_name: string | null;
  /** Description (may be empty string) */
  description: string;
  /** Dimension field path (e.g., "deals.deal_name") for native query construction */
  cube_field: string | null;
  /** Association dimensions only: entity type the association targets (e.g. Owner → ahoy_user) */
  target_entity_type?: string | null;
  /** Association dimensions only: entity kind the association targets (kind-based associations) */
  target_entity_kind?: string | null;
}

/**
 * Dimensions grouped by category for a single object type.
 */
export interface ObjectTypeDimensions {
  builtin: ReportDimension[];
  custom: ReportDimension[];
  cube: ReportDimension[];
  association: ReportDimension[];
  all: ReportDimension[];
}

/**
 * Full report metadata response from /api/orgs/{org_id}/report-metadata/
 * Keyed by entity type name (e.g., "ahoy_contact")
 */
export interface ReportMetadata {
  [entityTypeName: string]: ObjectTypeDimensions;
}

/**
 * Static display names for the hand-carded report sources ONLY. Custom
 * (org-defined) entity types are never listed here — their names come from
 * the org's object-type schema at runtime (see
 * pages/reports/wizard/useCustomReportSources.ts). Treat this map as the
 * "already offered statically" set, not a general label lookup.
 */
export const ENTITY_TYPE_DISPLAY_NAMES: Record<string, string> = {
  ahoy_contact: 'Contacts',
  ahoy_company: 'Companies',
  ahoy_deal: 'Deals',
  ahoy_note: 'Notes',
  ahoy_task: 'Tasks',
  ahoy_stage_timer: 'Deal Velocity',
};

/**
 * Native report query types (used when native_reporting feature flag is enabled)
 */
export interface NativeReportAggregation {
  function: string;
  property?: string;
  alias: string;
}

/**
 * A single group_by item. Mirrors backend `GroupByItemSerializer`:
 * - Exactly one of `property` or `association` must be set (mutually exclusive).
 * - `time_granularity` is only valid alongside `property`.
 */
export type NativeReportGroupBy =
  | {
      property: string;
      association?: never;
      time_granularity?: string;
    }
  | {
      association: string;
      property?: never;
      time_granularity?: never;
    };

/**
 * A relative date window, stored symbolically in a filter value instead of a
 * literal date and resolved server-side at execution time against the org's
 * calendar. This is what keeps a saved report's "Last 30 days" rolling rather
 * than freezing on the day it was saved.
 *
 * Mirrors `apps/crm_events/core/relative_dates.py`. Without `snap`: today
 * shifted by `offset` units. With `snap`: the period containing today, shifted
 * by `offset` periods, snapped to its first/last day.
 */
export interface RelativeDateValue {
  $relative: {
    unit: 'day' | 'week' | 'month' | 'quarter' | 'year';
    offset: number;
    snap?: 'start' | 'end';
  };
}

const RELATIVE_UNITS = new Set(['day', 'week', 'month', 'quarter', 'year']);

/**
 * True when a filter value is a relative date token.
 *
 * Deliberately the ONLY way any surface decides "is this relative?" — deriving
 * it from the value means it can never drift out of sync with the value the way
 * a hand-set marker flag would.
 */
export function isRelativeDateValue(
  value: unknown
): value is RelativeDateValue {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const token = (value as { $relative?: unknown }).$relative;
  if (typeof token !== 'object' || token === null) {
    return false;
  }
  const { unit, offset, snap } = token as {
    unit?: unknown;
    offset?: unknown;
    snap?: unknown;
  };
  return (
    typeof unit === 'string' &&
    RELATIVE_UNITS.has(unit) &&
    typeof offset === 'number' &&
    Number.isInteger(offset) &&
    (snap === undefined || snap === 'start' || snap === 'end')
  );
}

/**
 * A short, human-readable sentence-case label for a relative date token, e.g.
 * "Start of this month", "29 days ago", "In 30 days".
 *
 * Used by the read-only filter chip that renders a token which did not fold
 * back into a date-range dropdown selection.
 */
export function formatRelativeDateValue(value: RelativeDateValue): string {
  const { unit, offset, snap } = value.$relative;
  const plural = (n: number, noun: string) =>
    `${n} ${noun}${n === 1 ? '' : 's'}`;

  if (snap) {
    const prefix = snap === 'start' ? 'Start of' : 'End of';
    if (offset === 0) {
      return `${prefix} this ${unit}`;
    }
    if (offset === -1) {
      return `${prefix} last ${unit}`;
    }
    if (offset === 1) {
      return `${prefix} next ${unit}`;
    }
    return offset < 0
      ? `${prefix} ${plural(-offset, unit)} ago`
      : `${prefix} ${plural(offset, unit)} from now`;
  }

  if (offset === 0) {
    return 'Today';
  }
  if (unit === 'day' && offset === -1) {
    return 'Yesterday';
  }
  if (unit === 'day' && offset === 1) {
    return 'Tomorrow';
  }
  return offset < 0
    ? `${plural(-offset, unit)} ago`
    : `In ${plural(offset, unit)}`;
}

export interface FilterGroup {
  filters: Array<{
    property?: string;
    association?: string;
    target_property?: string;
    operator: string;
    value: unknown;
  }>;
  operator?: 'AND' | 'OR';
}

/**
 * A viewer-adjustable filter stored with the report (the Metabase
 * "filter widget" / Grafana "variable" pattern): the author designates a
 * dimension viewers can filter by from the view surface — without opening
 * the editor. Two variants, discriminated by which key is present:
 * `association` (pick a target entity) or `property` (a date/datetime
 * property viewers pick a time range for, encoded `start..end`).
 * Selections are ephemeral (URL-carried); `default_value` is the stored
 * default (null = All).
 */
export interface ReportViewFilter {
  /** Association type name (e.g. 'ahoy_deal_to_deal_pipeline'). */
  association?: string;
  /** Date/datetime property name (time-range filter, e.g. 'ahoy_close_date'). */
  property?: string;
  /** Control label shown to viewers (e.g. 'Pipeline', 'Close Date'). */
  label: string;
  /** Default selection (entity id, or 'start..end' range); null/absent = All. */
  default_value?: string | null;
}

/** The unique key a view filter's selection is stored under. */
export function viewFilterKey(filter: ReportViewFilter): string {
  return filter.association ?? filter.property ?? '';
}

export interface NativeReportQuery {
  entity_type_name: string;
  aggregations: NativeReportAggregation[];
  group_by?: Array<string | NativeReportGroupBy>;
  filter_groups?: FilterGroup[];
  sort_by?: string;
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  /** Viewer-adjustable filters (see ReportViewFilter). */
  view_filters?: ReportViewFilter[];
}

export interface ChartDatapointSelection {
  rowIndex: number;
  seriesKey: string;
}

export interface InsightChip {
  id: string;
  text: string;
  prompt: string;
  kind: 'insight' | 'onboarding';
}

export interface InsightChipsResponse {
  chips: InsightChip[];
}

// ChartType and Report types are owned here.
export type ChartType =
  | 'bar'
  | 'line'
  | 'pie'
  | 'table'
  | 'funnel'
  | 'area'
  | 'metric';

export interface Report {
  id: string;
  name: string;
  description: string;
  created_by_email?: string | null;
  // `query` is stored as the opaque on-the-wire JSON. A NativeReportQuery —
  // the single source of truth for the report — except on very old rows,
  // which may carry a legacy Cube query (their editor state hydrates from
  // `wizard_state` instead).
  query: NativeReportQuery | Record<string, unknown> | null;
  chart_type: ChartType;
  pivot_config: Record<string, unknown> | null;
  /**
   * Persisted partial ChartConfiguration merged over DEFAULT_CHART_CONFIG at
   * render; produced by the wizard layout control (multiMeasureLayout).
   * null/absent = defaults.
   */
  chart_options?: Partial<ChartConfiguration> | null;
  /**
   * Legacy editor-state blob, present only on rows saved before query-only
   * persistence (updates null it out). Read as a hydration fallback; never
   * written with a value.
   */
  wizard_state?: Record<string, unknown> | null;
  is_shared_with_org: boolean;
  folder: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface DashboardLayout {
  reportOrder?: string[];
  [key: string]: unknown;
}

export interface ReportDashboard {
  id: number;
  org: number;
  name: string;
  description: string;
  created_by: number | null;
  created_by_email?: string | null;
  layout: DashboardLayout;
  reports: string[];
  reports_data?: { id: number; entity_id: string }[];
  is_public: boolean;
  is_default: boolean;
  dismissed_default_report_keys: string[];
  created_at: string;
  updated_at: string;
}
